import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import { columnHelp } from '../columnHelp'
import { tableBodyRowClass } from '../tableRowStyles'
import { compareSortValues } from '../sortUtils'
import { SortHeaderButton } from './SortHeaderButton'

export interface SaberTableColumn<T> {
  key: string
  header: React.ReactNode
  render: (row: T) => React.ReactNode
  align?: 'left' | 'right' | 'center'
  className?: string
  /** Mark a column sortable (#4). Defaults the sort key to a string render. */
  sortable?: boolean
  /** Value used when sorting this column. Required for meaningful sortable columns. */
  sortValue?: (row: T) => string | number | null | undefined
}

export interface SaberTableSort {
  key: string
  direction: 'asc' | 'desc'
}

export interface SaberTableProps<T> {
  columns: Array<SaberTableColumn<T>>
  rows: T[]
  rowKey: (row: T, index: number) => string
  emptyMessage?: string
  className?: string
  /** Initial sort for sortable tables (#4). Falls back to the source row order. */
  defaultSort?: SaberTableSort
  /** Row click handler (#8). When set, rows get a pointer cursor. */
  onRowClick?: (row: T) => void
  /** Optional per-row class hook. */
  rowClassName?: (row: T) => string | undefined
  /** Alternate row background on odd-index rows for readability. */
  striped?: boolean
  /** WEB-6569: columnHelp table id; titles each header with its column's help text. */
  helpKey?: string
}

const alignClass = (align: SaberTableColumn<unknown>['align']): string => {
  switch (align) {
    case 'right':
      return 'text-right'
    case 'center':
      return 'text-center'
    default:
      return 'text-left'
  }
}

// Lightweight presentational table for SABER read screens. Light/dark aware.
// Supports opt-in click-to-sort columns (#4) and optional row clicks (#8).
export function SaberTable<T>({
  columns,
  rows,
  rowKey,
  emptyMessage = 'No data',
  className,
  defaultSort,
  onRowClick,
  rowClassName,
  striped = true,
  helpKey
}: SaberTableProps<T>) {
  const [sort, setSort] = useState<SaberTableSort | undefined>(defaultSort)

  const sortedRows = useMemo(() => {
    if (sort == null) return rows
    const col = columns.find((c) => c.key === sort.key)
    if (col?.sortValue == null) return rows
    return rows
      .map((row, index) => ({ row, index }))
      .sort((x, y) => {
        const cmp = compareSortValues(col.sortValue!(x.row), col.sortValue!(y.row), sort.direction)
        return cmp !== 0 ? cmp : x.index - y.index
      })
      .map((d) => d.row)
  }, [rows, columns, sort])

  const toggleSort = (col: SaberTableColumn<T>): void => {
    if (!col.sortable || col.sortValue == null) return
    setSort((prev) => {
      if (prev?.key !== col.key) return { key: col.key, direction: 'asc' }
      return { key: col.key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
    })
  }

  return (
    <div className={classnames('overflow-x-auto', className)}>
      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-sm">
        <thead>
          <tr>
            {columns.map((col) => {
              const isSortable = col.sortable === true && col.sortValue != null
              const active = sort?.key === col.key
              return (
                <th
                  key={col.key}
                  scope="col"
                  title={helpKey != null ? columnHelp(helpKey, col.key) : undefined}
                  aria-sort={
                    active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : isSortable ? 'none' : undefined
                  }
                  className={classnames(
                    'px-3 py-2 font-medium text-slate-500 dark:text-slate-400',
                    alignClass(col.align),
                    col.className
                  )}
                >
                  {isSortable ? (
                    <SortHeaderButton
                      label={col.header}
                      align={col.align}
                      active={active}
                      direction={sort?.direction ?? 'asc'}
                      onClick={() => toggleSort(col)}
                    />
                  ) : (
                    col.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {sortedRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-slate-400 dark:text-slate-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedRows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                onClick={onRowClick == null ? undefined : () => onRowClick(row)}
                className={classnames(
                  striped
                    ? tableBodyRowClass(index, onRowClick != null ? 'cursor-pointer' : undefined)
                    : classnames(
                        'transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/40',
                        onRowClick != null ? 'cursor-pointer' : undefined
                      ),
                  rowClassName?.(row)
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={classnames('px-3 py-2 text-slate-700 dark:text-slate-200', alignClass(col.align), col.className)}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

export default SaberTable
